/**
 * AttachmentsPanel — shared file-attachment UI for the Supplier &
 * Crew Info / Edit dialogs (2026-06-11).
 *
 * - Renders a list of files attached to a polymorphic record
 *   (``kind = "supplier" | "crew_member"``, ``id = "sup_..."`` / ``usr_...``).
 * - Optionally renders an upload button + per-row delete buttons
 *   (``editable`` prop). Read-only mode is used inside the Info modal;
 *   editable mode is used inside the Edit dialog.
 * - Re-uses the existing AuthFileLink for image/PDF viewing so we get
 *   the same authenticated download behaviour as elsewhere.
 *
 * Backend contract:
 *   GET    /api/attachments/{kind}/{id}/files     → list
 *   POST   /api/files/upload (kind, id form data) → upload
 *   DELETE /api/attachments/files/{file_id}       → hard-delete (storage + metadata)
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import FileUploader, { AuthFileLink, AuthImage } from "./FileUploader";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Trash2, FileText, Image as ImageIcon, Film, Loader2 } from "lucide-react";

const fmtErr = (e) => e?.response?.data?.detail || e?.message || "Something went wrong";

export default function AttachmentsPanel({ kind, refId, editable = false, title = "Files" }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [confirm, setConfirm] = useState(null); // {id, name}

  const load = useCallback(async () => {
    if (!kind || !refId) return;
    setLoading(true);
    setForbidden(false);
    try {
      const { data } = await api.get(`/attachments/${kind}/${refId}/files`);
      setFiles(data || []);
    } catch (e) {
      // Hide the panel entirely when the caller has no read access on
      // this record (the user is in our address book via legacy
      // crew_connections but we don't actually share a yacht right
      // now). Toast is reserved for unexpected failures.
      if (e?.response?.status === 403) {
        setForbidden(true);
        setFiles([]);
      } else {
        toast.error(fmtErr(e));
      }
    } finally {
      setLoading(false);
    }
  }, [kind, refId]);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (file) => {
    try {
      await api.delete(`/attachments/files/${file.id}`);
      toast.success("File removed");
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <div data-testid={`attachments-${kind}`}>
      {forbidden ? null : (
      <>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{title}</div>
        {editable && (
          <FileUploader
            attachment_kind={kind}
            attachment_id={refId}
            folder="attachments"
            accept="image/*,video/*,application/pdf"
            enableVideo
            label="Upload file"
            testid={`attachments-upload-${kind}`}
            onUploaded={() => load()}
          />
        )}
      </div>
      {loading ? (
        <div className="text-zinc-400 text-xs flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading files…
        </div>
      ) : files.length === 0 ? (
        <div className="text-zinc-400 text-xs">No files attached yet.</div>
      ) : (
        <ul className="space-y-2" data-testid={`attachments-list-${kind}`}>
          {files.map((f) => {
            const isImage = (f.content_type || "").startsWith("image/");
            const isVideo = (f.content_type || "").startsWith("video/");
            const isPdf = f.content_type === "application/pdf";
            const sizeKb = Math.round((f.size || 0) / 1024);
            return (
              <li
                key={f.id}
                className="flex items-center justify-between gap-3 rounded-md border border-zinc-200 bg-white px-3 py-2"
                data-testid={`attachment-row-${f.id}`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {isImage ? (
                    <AuthImage
                      path={f.storage_path}
                      className="h-10 w-10 rounded object-cover bg-zinc-100 shrink-0"
                      alt={f.original_filename || "image"}
                    />
                  ) : (
                    <span className="shrink-0 grid place-items-center h-10 w-10 rounded bg-zinc-100 text-zinc-500">
                      {isPdf ? <FileText className="h-4 w-4" /> : isVideo ? <Film className="h-4 w-4" /> : <ImageIcon className="h-4 w-4" />}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <AuthFileLink
                      path={f.storage_path}
                      className="text-sm truncate block"
                      testid={`attachment-open-${f.id}`}
                    >
                      {f.original_filename || f.storage_path.split("/").pop()}
                    </AuthFileLink>
                    <div className="text-[11px] text-zinc-500">
                      {sizeKb} KB · {(f.created_at || "").slice(0, 10)}
                    </div>
                  </div>
                </div>
                {editable && (
                  <button
                    type="button"
                    onClick={() => setConfirm({ id: f.id, name: f.original_filename })}
                    className="shrink-0 p-1.5 rounded text-zinc-400 hover:text-red-600 hover:bg-red-50"
                    title="Remove file"
                    data-testid={`attachment-delete-${f.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      </>
      )}
      {confirm && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4" onClick={() => setConfirm(null)}>
          <div
            className="bg-white rounded-md max-w-sm w-full p-5 space-y-4 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <div className="font-semibold">Remove file?</div>
              <div className="text-sm text-zinc-600 mt-1">
                {confirm.name ? `"${confirm.name}" ` : ""}will be permanently removed from storage and cannot be recovered.
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={() => onDelete({ id: confirm.id })}
                data-testid="attachment-confirm-delete"
              >
                Remove
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
